import React, { useReducer, useMemo, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, shallowEqual } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { LinearProgress } from '@totalsoft/rocket-ui';

const defaultData = {};
const ActivityIndicatorContext = React.createContext();
const ActivityIndicatorDispatchContext = React.createContext();

const reducer = (state, action) => {
  const key = action.key ?? '-';
  const callsCount = state[key]?.callsInProgress ?? 0;

  switch (action.type) {
    case 'onBeginCall': {
      return {
        ...state,
        [key]: { callsInProgress: callsCount + 1 }
      };
    }
    case 'onEndCall': {
      return {
        ...state,
        [key]: { callsInProgress: callsCount > 0 ? callsCount - 1 : callsCount }
      };
    }
    default: {
      return state;
    }
  }
};

const dispatchActions = dispatch => ({
  onBeginCall: key => dispatch({ type: 'onBeginCall', key }),
  onEndCall: key => dispatch({ type: 'onEndCall', key })
});

const useActivityIndicatorContext = () => {
  const location = useLocation();
  const pageKey = location.pathname;
  const { onBeginCall: obc, onEndCall: oec } = useContext(ActivityIndicatorDispatchContext);

  const onBeginCall = useCallback(() => obc(pageKey), [obc, pageKey]);
  const onEndCall = useCallback(() => oec(pageKey), [oec, pageKey]);

  return { onBeginCall, onEndCall };
};

const ActivityIndicator = () => {
  const location = useLocation();
  const pageKey = location.pathname;

  const { [pageKey]: target } = useContext(ActivityIndicatorContext);
  const callsInProgress = target?.callsInProgress ?? 0;
  const ajaxCallsInProgress = useSelector(state => state.ajaxCallsInProgress, shallowEqual);
  const loading = useMemo(() => ajaxCallsInProgress + callsInProgress > 0, [ajaxCallsInProgress, callsInProgress]);

  return <>{loading && <LinearProgress global color='primary' />}</>;
};

const ActivityIndicatorProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, defaultData);
  const actions = useMemo(() => dispatchActions(dispatch), [dispatch]);

  return (
    <ActivityIndicatorContext.Provider value={state}>
      <ActivityIndicatorDispatchContext.Provider value={actions}>
        <>
          <ActivityIndicator />
          {children}
        </>
      </ActivityIndicatorDispatchContext.Provider>
    </ActivityIndicatorContext.Provider>
  );
};

ActivityIndicatorProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export { ActivityIndicatorProvider, useActivityIndicatorContext };
export default ActivityIndicatorProvider;
